'use client';

import React, { useEffect, useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import SiteHeader from '../../../components/SiteHeader';
import { StoreFooter } from '../../../components/StoreChrome';
import { InstagramLogo } from '../../../components/SocialLogos';
import { InstagramMedia, listarMidiasInstagram, dataInstagram } from '../../../lib/instagram-api';

// Mesmo handle usado no rodape da home, no /contato e no OrganizationSchema.
const PERFIL_URL = 'https://www.instagram.com/enq.digital/';
// Embed oficial do perfil (iframe do proprio Instagram, sem token).
const EMBED_URL = `${PERFIL_URL}embed/`;

function IconePlay() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5v14l11-7z" />
    </svg>
  );
}

function IconeCarrossel() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <rect x="7" y="7" width="12" height="12" rx="2" />
      <path d="M4 16V6a2 2 0 0 1 2-2h10" />
    </svg>
  );
}

function CardMidia({ midia }: { midia: InstagramMedia }) {
  const t = useTranslations('paginaMidia');
  const locale = useLocale();
  const src = midia.thumbnailUrl || midia.mediaUrl || '';
  const isVideo = midia.isReel || midia.mediaType === 'VIDEO';
  const isCarrossel = midia.mediaType === 'CAROUSEL_ALBUM';
  const legenda = midia.caption.trim();

  return (
    <a
      href={midia.permalink}
      target="_blank"
      rel="noopener noreferrer"
      className="group block bg-white"
      style={{ border: '1px solid #dbdbdb', borderRadius: '8px', overflow: 'hidden' }}
      aria-label={t('verNoInstagram')}
    >
      <div className="relative" style={{ aspectRatio: '1 / 1', background: '#eeeeee' }}>
        {src ? (
          <img
            src={src}
            alt={legenda ? legenda.slice(0, 120) : t('semLegenda')}
            loading="lazy"
            className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-400">
            <InstagramLogo size={32} />
          </div>
        )}

        {isVideo && (
          <span
            className="absolute flex items-center justify-center text-white"
            style={{
              top: '8px',
              right: '8px',
              width: '28px',
              height: '28px',
              borderRadius: '999px',
              background: 'rgba(0,0,0,0.55)',
            }}
            title={t('reelBadge')}
          >
            <IconePlay />
          </span>
        )}
        {isCarrossel && !isVideo && (
          <span
            className="absolute flex items-center justify-center text-white"
            style={{
              top: '8px',
              right: '8px',
              width: '28px',
              height: '28px',
              borderRadius: '999px',
              background: 'rgba(0,0,0,0.55)',
            }}
            title={t('carrosselBadge')}
          >
            <IconeCarrossel />
          </span>
        )}
      </div>

      <div style={{ padding: '10px 12px 12px' }}>
        {legenda && (
          <p
            className="text-gray-700"
            style={{
              fontSize: '13px',
              lineHeight: 1.45,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {legenda}
          </p>
        )}
        <span className="block text-gray-400 mt-1" style={{ fontSize: '11px' }}>
          {dataInstagram(midia.timestamp, locale)}
        </span>
      </div>
    </a>
  );
}

export default function MidiaPage() {
  const t = useTranslations('paginaMidia');
  const [midias, setMidias] = useState<InstagramMedia[]>([]);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    let ativo = true;
    listarMidiasInstagram(12)
      .then((lista) => {
        if (ativo) setMidias(lista);
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, []);

  return (
    <>
      <SiteHeader />
      <main className="bg-white" style={{ minHeight: '70vh' }}>
        <div className="container-site" style={{ padding: '48px 0 32px' }}>
          <p className="eyebrow mb-3" style={{ color: '#1a9fff' }}>
            {t('eyebrow')}
          </p>
          <h1 className="display-section mb-4" style={{ maxWidth: '760px' }}>
            {t('titulo')}
          </h1>
          <p
            className="text-gray-600"
            style={{ fontSize: '16px', lineHeight: 1.7, maxWidth: '620px' }}
          >
            {t('descricao')}
          </p>
          <a
            href={PERFIL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm font-semibold mt-4"
            style={{ color: '#1a9fff' }}
          >
            <InstagramLogo size={16} />
            {t('verPerfil')}
          </a>
        </div>

        <div className="container-site" style={{ padding: '0 0 80px' }}>
          {carregando ? (
            <p className="text-gray-500" style={{ fontSize: '14px' }}>
              {t('carregando')}
            </p>
          ) : midias.length === 0 ? (
            // A API (Graph API, token no .env) nao devolveu nada - ex.: token
            // expirado/invalidado pela Meta. Em vez de uma pagina vazia, mostra o
            // embed oficial do perfil, que nao depende de token e ja abre com as
            // publicacoes. Quando a API voltar a responder, a grade nativa abaixo
            // reassume sozinha.
            <div className="text-center">
              <iframe
                src={EMBED_URL}
                title={t('embedTitulo')}
                loading="eager"
                className="mx-auto block w-full"
                style={{
                  maxWidth: '760px',
                  height: 'clamp(560px, 110vw, 820px)',
                  border: '1px solid #dbdbdb',
                  borderRadius: '8px',
                  background: '#fff',
                }}
              />
              <a
                href={PERFIL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary inline-flex items-center gap-2 mt-6"
              >
                <InstagramLogo size={16} />
                {t('verPerfil')}
              </a>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {midias.map((m) => (
                <CardMidia key={m.id} midia={m} />
              ))}
            </div>
          )}
        </div>
      </main>
      <StoreFooter />
    </>
  );
}
